<template>
  <div>
    <div class="card">
      <div class="card-header card-header-flex">
        <div class="d-flex flex-column justify-content-center mr-auto">
          <h5 class="mb-0">
            {{ $t('returns.self') }}
          </h5>
        </div>
      </div>
      <div class="card-body">
        <a-table
          :loading="loading"
          :data-source="data"
          :columns="columns"
          :pagination="pagination"
          bordered
          :scroll="{x: 1280}"
          @change="handleTableChange"
        >
          <div
            slot="filterDropdown"
            slot-scope="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }"
            style="padding: 8px"
          >
            <a-input
              v-ant-ref="c => (searchInput = c)"
              :placeholder="`Search ${column.dataIndex}`"
              :value="selectedKeys[0]"
              style="width: 188px; margin-bottom: 8px; display: block;"
              @change="e => setSelectedKeys(e.target.value ? [e.target.value] : [])"
              @pressEnter="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            />
            <a-button
              type="primary"
              icon="search"
              size="small"
              style="width: 90px; margin-right: 8px"
              @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            >
              {{ $t('common.search') }}
            </a-button>
            <a-button
              size="small"
              style="width: 90px"
              @click="() => handleReset(clearFilters)"
            >
              {{ $t('common.reset') }}
            </a-button>
          </div>
          <a-icon
            slot="filterIcon"
            slot-scope="filtered"
            type="search"
            :style="{ color: filtered ? '#108ee9' : undefined }"
          />
          <template
            slot="date"
            slot-scope="text"
          >
            <span v-if="text">{{ text | dateFormatter('DD-MM-YYYY HH:mm') }}</span>
          </template>
          <template
            slot="phone"
            slot-scope="text"
          >
            <span>{{ text | phoneFormatter() }}</span>
          </template>
          <template
            slot="sellerName"
            slot-scope="text"
          >
            <ShrinkStringDisplay :string="text" />
          </template>
          <template
            slot="verdict"
            slot-scope="text"
          >
            <ReturnStatus :status="text" />
          </template>
          <template
            slot="details"
            slot-scope="text"
          >
            <router-link :to="`returns/${text}`">
              <b-button
                size="sm"
                variant="light"
                class="mr-2"
              >
                <i class="fe fe-edit mr-2" />
                {{ $t('common.view') }}
              </b-button>
            </router-link>
          </template>
        </a-table>
      </div>
    </div>
  </div>
</template>
<script>
import OrderService from '@/services/api/manager/order-service'
import ShrinkStringDisplay from '@/components/common/ShrinkStringDisplay'
import statusList from '@/utils/returnStatusList'
import { mapGetters } from 'vuex'

import ReturnStatus from '@/components/order/ReturnStatus'

export default {
  name: 'ReturnApplications',
  components: {
    ShrinkStringDisplay,
    ReturnStatus,
  },
  data: function () {
    return {
      searchText: '',
      data: [],
      columns: [
        {
          title: `${this.$t('returns.application')} №`,
          dataIndex: 'id',
          key: 'id',
          width: 80,
        },
        {
          title: this.$t('returns.date_created'),
          dataIndex: 'applicationDate',
          key: 'date_created',
          scopedSlots: {
            customRender: 'date',
          },
          sorter: true,
          defaultSortOrder: 'descend',
        },
        {
          title: this.$t('orders.self'),
          children: [
            {
              title: `${this.$t('orders.order')} №`,
              dataIndex: 'order_id',
              key: 'order_id',
              scopedSlots: {
                filterDropdown: 'filterDropdown',
                filterIcon: 'filterIcon',
              },
            },
            {
              title: this.$t('reports.operations.track_no'),
              dataIndex: 'trackingNumber',
              key: 'tracking_number',
              scopedSlots: {
                filterDropdown: 'filterDropdown',
                filterIcon: 'filterIcon',
              },
            },
            {
              title: this.$t('returns.seller'),
              dataIndex: 'sellerName',
              key: 'sellerName',
              scopedSlots: {
                customRender: 'sellerName',
              },
            },
          ],
        },
        {
          title: this.$t('orders.change_log'),
          children: [
            {
              title: this.$t('returns.order_accepted'),
              key: 'orderAccepted',
              dataIndex: 'chronicle.orderAccepted',
              scopedSlots: {
                customRender: 'date',
              },
            },
            {
              title: this.$t('returns.order_delivered'),
              key: 'orderDelivered',
              dataIndex: 'chronicle.delivered',
              scopedSlots: {
                customRender: 'date',
              },
            },
          ],
        },
        {
          title: this.$t('orders.customer'),
          children: [
            {
              title: this.$t('users.name'),
              dataIndex: 'buyerName',
              key: 'buyerName',
            },
            {
              title: this.$t('users.phone'),
              dataIndex: 'buyerPhone',
              key: 'phone',
              scopedSlots: {
                filterDropdown: 'filterDropdown',
                filterIcon: 'filterIcon',
                customRender: 'phone',
              },
            },
          ],
        },
        {
          title: this.$t('returns.verdict'),
          dataIndex: 'verdict',
          key: 'verdict',
          width: 160,
          scopedSlots: {
            customRender: 'verdict',
          },
          filters: Object.entries(statusList).map(([key, value]) => {
            return {
              text: this.$t(value.title),
              value: key,
            }
          }),
        },
        {
          title: this.$t('returns.details'),
          dataIndex: 'hashId',
          key: 'details',
          scopedSlots: {
            customRender: 'details',
          },
        },
      ],
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
      },
      sorting: {
        col: 'date_created',
        order: 'desc',
      },
      filters: {},
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/user',
    }),
  },
  mounted() {
    this.fetchReturnApplications()
  },
  methods: {
    handleHighlight(item, index) {
      if (item && item.remainder === 0)
        return 'tr-highlight'
    },

    onFilterDropdownVisibleChange (visible) {
      if (visible) {
        setTimeout(() => {
          this.searchInput.focus()
        }, 0)
      }
    },

    handleSearch(selectedKeys, confirm) {
      this.searchText = selectedKeys[0]
      confirm()
    },

    handleReset(clearFilters) {
      this.searchText = ''
      clearFilters()
    },

    handleTableChange (pagination, filter, sorter) {
      if (sorter.columnKey && sorter.order)
        this.sorting = {
          col: sorter.columnKey,
          order: sorter.order.replace('end', ''), // ascend -> asc | descend -> desc
        }

      Object.keys(filter).forEach(key => {
        if (filter[key] && filter[key].length > 0)
          this.filters[key] = filter[key]
        else
          this.filters[key] = null
      })

      this.pagination.current = pagination.current

      this.fetchReturnApplications()
    },

    fetchReturnApplications() {
      this.loading = true
      OrderService.getReturnApplications(this.pagination, this.sorting, this.filters)
        .then((response) => {
          if (response && response.data && response.data.content) {
            this.pagination.total = response.data.pagination.total
            this.data = response.data.content.map((application, index) => {
              return {
                id: application.id,
                order_id: application.order.long_id,
                hashId: application.hash_id,
                key: index,
                applicationDate: parseInt(application.date_created),
                trackingNumber: application.order.tracking_number,
                sellerName: this.$l(application.order.company.title),
                chronicle: {
                  orderAccepted: application.date_in_progress,
                  delivered: application.date_delivered,
                },
                buyerName: application.buyer.name,
                buyerPhone: application.phone,
                verdict: application.verdict || 'PROCESSING_RETURN',
              }
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>
<style scoped>
  .card {
    overflow-x: scroll;
  }
</style>
